import MembersFiltersBar from "./members/MembersFiltersBar";
import InviteMembersModal from "./members/InviteMembersModal";
import MembersEmptyState from "./members/MembersEmptyState";
import PermissionsPickerModal from "./shared/PermissionsPickerModal";
import RolesSelectionModal from "./members/RolesSelectionModal";
import MemberDetailsFlyin from "./members/MemberDetailsFlyin";
import AssignRoleModal from "./members/AssignRoleModal";
import AssignAccountManagerModal from "./members/AssignAccountManagerModal";
import DeleteMemberModal from "./members/DeleteMemberModal";
import ManageMemberCustomersModal from "./members/ManageMemberCustomersModal";
import ReassignCustomersModal from "./members/ReassignCustomersModal";
import { DataTable } from "../../../shared/ui/DataTable";
import { PaginationControls, Shimmer } from "../../../shared/ui";
import { useMembersSectionController } from "./members/useMembersSectionController";

const MembersLoadingShimmer = () => {
  return (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="flex items-center gap-4">
          <Shimmer className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Shimmer className="h-4 w-[220px]" />
            <Shimmer className="h-3 w-[180px]" />
          </div>
          <Shimmer className="h-8 w-[120px]" />
          <Shimmer className="h-8 w-[100px]" />
          <Shimmer className="h-8 w-8 rounded-full" />
        </div>
      ))}
    </div>
  );
};

export default function MembersSection() {
  const controller = useMembersSectionController();

  return (
    <section className="w-full">
      <MembersFiltersBar
        query={controller.query}
        onQueryChange={controller.setQuery}
        sort={controller.sort}
        onSortChange={controller.setSort}
        sortOptions={controller.sortOptions}
        roleTypes={controller.roleTypes}
        onRoleTypesChange={controller.setRoleTypes}
        roleTypeOptions={controller.roleTypeOptions}
        onInviteClick={controller.openInviteModal}
      />

      {controller.isMembersLoading ? (
        <DataTable
          columns={controller.columns}
          data={[]}
          getRowKey={(m) => m.id}
          onRowClick={controller.openMemberFlyin}
          emptyState={<MembersLoadingShimmer />}
        />
      ) : controller.filteredMembers.length === 0 ? (
        <MembersEmptyState
          variant={controller.totalMembersCount === 0 ? "none" : "search"}
        />
      ) : (
        <DataTable
          columns={controller.columns}
          data={controller.pagedMembers}
          getRowKey={(m) => m.id}
          onRowClick={controller.openMemberFlyin}
        />
      )}

      {!controller.isMembersLoading && controller.filteredMembers.length > 0 ? (
        <PaginationControls
          currentPage={controller.currentPage}
          totalPages={controller.totalMemberPages}
          onPageChange={controller.setCurrentPage}
          className="mt-8 flex items-center gap-2"
        />
      ) : null}

      <InviteMembersModal
        open={controller.isInviteOpen}
        onOpenChange={controller.setIsInviteOpen}
        emailInput={controller.emailInput}
        onEmailInputChange={controller.setEmailInput}
        onEmailKeyDown={controller.handleEmailKeyDown}
        onEmailBlur={() => {
          if (controller.emailInput.trim()) {
            controller.handleAddEmail(controller.emailInput);
            controller.setEmailInput("");
          }
        }}
        emails={controller.emails}
        onRemoveEmail={controller.removeEmail}
        selectedRoles={controller.selectedRoles}
        onRemoveSelectedRole={(roleName) =>
          controller.setSelectedRoles((prev) =>
            prev.filter((item) => item !== roleName),
          )
        }
        onOpenRolesModal={() => controller.setIsRolesModalOpen(true)}
        isRolesDisabled={controller.rolesForUi.length === 0}
        selectedAdditionalPermissions={controller.selectedAdditionalPermissions}
        onOpenPermissionsModal={() => {
          controller.setExpandedGroupId("");
          controller.setIsPermissionsModalOpen(true);
        }}
        onCancel={() => {
          controller.setInviteError(null);
          controller.setIsInviteOpen(false);
        }}
        onInvite={controller.handleInviteMembers}
        canInvite={controller.canInviteMembers}
        isInviting={controller.isInviting}
        inviteError={controller.inviteError}
      />

      <PermissionsPickerModal
        open={controller.isPermissionsModalOpen}
        onOpenChange={controller.setIsPermissionsModalOpen}
        searchQuery={controller.permissionQuery}
        onSearchQueryChange={controller.setPermissionQuery}
        groups={controller.filteredPermissionGroups}
        isLoading={controller.isPermissionsLoading}
        error={controller.permissionsError}
        expandedGroupId={controller.expandedGroupId}
        onExpandedGroupIdChange={controller.setExpandedGroupId}
        isPermissionSelected={(permission) =>
          controller.selectedPermissionsForInvite.includes(permission)
        }
        onTogglePermission={controller.toggleInvitePermission}
        onToggleGroupPermissions={controller.toggleInviteGroupPermissions}
        selectedCount={controller.selectedPermissionsForInvite.length}
        helperText={
          controller.selectedRoles.length > 0
            ? "Permissions from selected roles are pre-selected."
            : undefined
        }
        permissionSuffix={(permission) =>
          controller.rolePermissions.includes(permission) ? " (from role)" : ""
        }
      />

      <PermissionsPickerModal
        open={controller.isMemberPermissionsModalOpen}
        onOpenChange={(open) => {
          if (!open && controller.isSavingMemberPermissions) return;
          controller.setIsMemberPermissionsModalOpen(open);
          if (!open) controller.setSaveMemberPermissionsError(null);
        }}
        title={`Add permissions for ${controller.activeMember?.name ?? "member"}`}
        searchQuery={controller.memberPermissionsQuery}
        onSearchQueryChange={controller.setMemberPermissionsQuery}
        groups={controller.filteredPermissionGroupsForMember}
        isLoading={controller.isPermissionsLoading}
        error={
          controller.saveMemberPermissionsError ?? controller.permissionsError
        }
        expandedGroupId={controller.memberExpandedPermissionGroupId}
        onExpandedGroupIdChange={controller.setMemberExpandedPermissionGroupId}
        isPermissionSelected={(permission) =>
          controller.editingMemberPermissions.includes(permission)
        }
        onTogglePermission={controller.toggleMemberPermission}
        onToggleGroupPermissions={controller.toggleMemberPermissionGroup}
        selectedCount={controller.editingMemberPermissions.length}
        secondaryLabel="Cancel"
        primaryLabel={
          controller.isSavingMemberPermissions
            ? "Saving..."
            : "Save permissions"
        }
        onSecondaryAction={() => {
          if (controller.isSavingMemberPermissions) return;
          controller.setIsMemberPermissionsModalOpen(false);
          controller.setSaveMemberPermissionsError(null);
        }}
        onPrimaryAction={controller.handleSaveMemberPermissions}
      />

      <RolesSelectionModal
        open={controller.isRolesModalOpen}
        onOpenChange={controller.setIsRolesModalOpen}
        roles={controller.rolesForUi}
        selectedRoles={controller.selectedRoles}
        onToggleRoleSelection={controller.toggleRoleSelection}
        className="z-[60]"
      />

      <MemberDetailsFlyin
        open={controller.isMemberFlyinOpen}
        onOpenChange={controller.setIsMemberFlyinOpen}
        activeMember={controller.activeMember}
        onAssignAccountManager={() => {
          if (!controller.activeMember) return;
          controller.openAssignAccountManagerForMember(controller.activeMember);
        }}
        onAssignRole={() => {
          if (!controller.activeMember) return;
          controller.openEditRolesForMember(controller.activeMember);
        }}
        onAddPermissions={() => {
          if (!controller.activeMember) return;
          controller.openAddPermissionsForMember(controller.activeMember);
        }}
        onResendInvite={() => {
          if (!controller.activeMember) return;
          controller.handleResendInvite(controller.activeMember);
        }}
        onRemoveMember={() => {
          if (!controller.activeMember) return;
          controller.openRemoveMemberFlow(controller.activeMember);
        }}
        onChangeCustomerAccountManager={
          controller.openChangeCustomerAccountManager
        }
      />

      <AssignRoleModal
        open={controller.isAssignRoleOpen}
        onOpenChange={controller.setIsAssignRoleOpen}
        memberName={controller.activeMember?.name ?? "Member"}
        roles={controller.rolesForAssignRoleModal}
        selectedRoles={controller.editingMemberRoles}
        onToggleRoleSelection={controller.toggleEditingMemberRole}
        onCancel={() => {
          if (controller.isSavingMemberRoles) return;
          controller.setIsAssignRoleOpen(false);
          controller.setSaveMemberRolesError(null);
        }}
        onSave={controller.handleSaveMemberRoles}
        isSaving={controller.isSavingMemberRoles}
        error={controller.saveMemberRolesError}
      />

      <AssignAccountManagerModal
        open={controller.isAssignAccountManagerOpen}
        onOpenChange={(open) => {
          if (!open && controller.isAssigningCustomers) return;
          controller.setIsAssignAccountManagerOpen(open);
          if (!open) {
            controller.setAssignCustomersError(null);
            controller.setSelectedBusinessIdsForManagerAssign([]);
          }
        }}
        memberName={controller.activeMember?.name ?? "Member"}
        allBusinesses={controller.unassignedBusinessesForActiveMember}
        selectedBusinessIds={controller.selectedBusinessIdsForManagerAssign}
        onToggleBusiness={controller.toggleManagerAssignBusiness}
        onCancel={() => {
          if (controller.isAssigningCustomers) return;
          controller.setIsAssignAccountManagerOpen(false);
          controller.setAssignCustomersError(null);
        }}
        onAssign={controller.handleAssignCustomers}
        isAssigning={controller.isAssigningCustomers}
        error={controller.assignCustomersError}
      />

      <DeleteMemberModal
        open={controller.isDeleteMemberOpen}
        onOpenChange={(open) => {
          if (
            (controller.isDeletingMember ||
              controller.isReassigningDeletionCustomers) &&
            !open
          )
            return;
          if (!open) controller.resetDeleteMemberFlow();
          controller.setIsDeleteMemberOpen(open);
        }}
        memberName={controller.memberPendingRemoval?.name ?? "Member"}
        managedCustomersCount={
          controller.memberPendingRemoval?.businesses.length ?? 0
        }
        remainingCustomersCount={
          controller.remainingPendingRemovalCustomersCount
        }
        canDelete={controller.canDeletePendingMember}
        selectedMemberId={controller.bulkReassignmentMemberId}
        memberOptions={controller.memberOptionsForReassignment}
        memberQuery={controller.bulkReassignmentMemberQuery}
        isMemberDropdownOpen={controller.isBulkReassignmentDropdownOpen}
        onMemberDropdownOpenChange={
          controller.setIsBulkReassignmentDropdownOpen
        }
        onMemberQueryChange={controller.setBulkReassignmentMemberQuery}
        onSelectReassignmentMember={(memberId) => {
          controller.setReassignDeletionCustomersError(null);
          controller.setBulkReassignmentMemberId(memberId);
        }}
        onViewCustomers={controller.openManageCustomersForReassignment}
        onCancel={controller.resetDeleteMemberFlow}
        onDelete={controller.handleDeleteMember}
        isDeleting={
          controller.isDeletingMember ||
          controller.isReassigningDeletionCustomers
        }
        error={
          controller.reassignDeletionCustomersError ??
          controller.deleteMemberError
        }
      />

      <ManageMemberCustomersModal
        open={controller.isManageMemberCustomersOpen}
        onOpenChange={(open) => {
          if (
            (controller.isDeletingMember ||
              controller.isReassigningDeletionCustomers) &&
            !open
          )
            return;
          controller.setIsManageMemberCustomersOpen(open);
          if (!open) {
            controller.setIsDeleteMemberOpen(true);
          }
        }}
        memberName={controller.memberPendingRemoval?.name ?? "Member"}
        businesses={controller.pendingRemovalBusinesses}
        selectedBusinessIds={controller.selectedBusinessesForReassignment}
        query={controller.managedCustomersQuery}
        onQueryChange={controller.setManagedCustomersQuery}
        onToggleBusiness={controller.toggleBusinessForReassignment}
        onBack={() => {
          controller.setIsManageMemberCustomersOpen(false);
          controller.setIsDeleteMemberOpen(true);
        }}
        onReassignCustomers={controller.openReassignSelectedCustomers}
      />

      <ReassignCustomersModal
        open={controller.isReassignCustomersOpen}
        onOpenChange={(open) => {
          if (controller.isReassigningDeletionCustomers && !open) return;
          controller.setIsReassignCustomersOpen(open);
          if (!open) {
            controller.setIsManageMemberCustomersOpen(true);
          }
        }}
        selectedCustomersCount={
          controller.selectedBusinessesForReassignment.length
        }
        selectedMemberId={controller.selectedReassignmentMemberId}
        memberOptions={controller.memberOptionsForReassignmentList}
        memberQuery={controller.selectedReassignmentMemberQuery}
        isMemberDropdownOpen={controller.isSelectedReassignmentDropdownOpen}
        onMemberDropdownOpenChange={
          controller.setIsSelectedReassignmentDropdownOpen
        }
        onMemberQueryChange={controller.setSelectedReassignmentMemberQuery}
        onSelectMember={controller.setSelectedReassignmentMemberId}
        onBackToCustomers={() => {
          controller.setIsReassignCustomersOpen(false);
          controller.setIsManageMemberCustomersOpen(true);
        }}
        onCancel={controller.resetDeleteMemberFlow}
        onSaveChanges={controller.saveSelectedCustomerReassignment}
        isSaving={controller.isReassigningDeletionCustomers}
        error={controller.reassignDeletionCustomersError}
      />

      <ReassignCustomersModal
        open={controller.isFlyinCustomerReassignOpen}
        onOpenChange={(open) => {
          if (!open && controller.isReassigningFlyinCustomer) return;
          controller.setIsFlyinCustomerReassignOpen(open);
          if (!open) {
            controller.setIsMemberFlyinOpen(true);
          }
        }}
        selectedCustomersCount={1}
        selectedMemberId={controller.flyinReassignMemberId}
        memberOptions={controller.memberOptionsForFlyinReassignment}
        memberQuery={controller.flyinReassignMemberQuery}
        isMemberDropdownOpen={controller.isFlyinReassignDropdownOpen}
        onMemberDropdownOpenChange={controller.setIsFlyinReassignDropdownOpen}
        onMemberQueryChange={controller.setFlyinReassignMemberQuery}
        onSelectMember={controller.setFlyinReassignMemberId}
        onBackToCustomers={() => {
          if (controller.isReassigningFlyinCustomer) return;
          controller.setIsFlyinCustomerReassignOpen(false);
          controller.setIsMemberFlyinOpen(true);
        }}
        onCancel={() => {
          if (controller.isReassigningFlyinCustomer) return;
          controller.resetFlyinCustomerReassignFlow();
          controller.setIsMemberFlyinOpen(true);
        }}
        onSaveChanges={controller.handleSaveFlyinCustomerReassignment}
        isSaving={controller.isReassigningFlyinCustomer}
        error={controller.reassignFlyinCustomerError}
      />
    </section>
  );
}
